import { ApolloCache, DefaultContext, MutationUpdaterFunction } from '@apollo/client';
import {
  colors,
  ControlledAutocompleteInput,
  ControlledInput,
  ControlledTextArea,
  InputFormContainer,
  Stack,
  Tooltip,
  Typography,
} from '@ui-components/neo.tax';
import { ReactComponent as InfoIcon } from '@ui-components/neo.tax/dist/icons/info-circle.svg';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { useAscContext } from '@neo.tax/asc/context';
import { useUpdateAscProject, type AscProject } from '@neo.tax/asc/hooks';
import { getCapitalizationDateString, PROJECT_DATES_DESCRIPTION } from '@neo.tax/asc/organisms/asc-projects-layout';
import {
  evictCache,
  Exact,
  GetAscProjectsQuery,
  GetAscProjectsQueryVariables,
  GET_ASC_PROJECTS,
  noopFormSubmit,
  ToastConfigState,
  useCurrentCompanyId,
  UpdateProductMutation,
  DeleteProductModal,
  UpdateProductModal,
  useCreateProduct,
  useProductOptions,
} from '@neo.tax/shared';

import { CapitalizationEditModal } from '../capitalization-edit-modal';
import {
  StyledCapitalizationDatesStack,
  StyledCapitalizationStatusStack,
  StyledDateCellStack,
  StyledProjectDatesSpacerStack,
  StyledProjectDatesStack,
} from './asc-project-detail-form.styled';
import { AscProjectInfoForm } from './asc-project-detail-form.types';
import { makeDefaultFormValues } from './asc-project-detail-form.utils';
import { useAscProjectDetailAnalyticIds } from './hooks';

interface AscProjectDetailFormProps {
  ascProject: AscProject;
}

const UPDATE_ASC_PROJECT_REQUEST_KEY = 'UPDATE_ASC_PROJECT_REQUEST_KEY';

export const AscProjectDetailForm = ({ ascProject }: AscProjectDetailFormProps): ReactElement => {
  const companyId = useCurrentCompanyId();
  const { fiscalYearId, periodName } = useAscContext();
  const { clearSelectedProductState, productOptions, productToDelete, productToEdit } = useProductOptions();
  const [createProduct] = useCreateProduct();
  const [isCapitalizationModalOpen, setIsCapitalizationModalOpen] = useState(false);
  const { clearProductBtnId, descriptionId, nameId, productId } = useAscProjectDetailAnalyticIds();
  const { control, handleSubmit, reset, setValue, watch } = useForm<AscProjectInfoForm>({
    mode: 'onChange',
    defaultValues: makeDefaultFormValues(ascProject),
  });

  const [updateAscProject] = useUpdateAscProject({
    ascProject,
    fiscalYearId,
    periodName,
    context: {
      debounceKey: UPDATE_ASC_PROJECT_REQUEST_KEY,
      serializationKey: UPDATE_ASC_PROJECT_REQUEST_KEY,
    },
  });

  const handleClearProduct = (): void => {
    setValue('productId', null);
  };

  const onDeleteProduct = (cache: ApolloCache<any>): void => {
    const ascProjects = cache.readQuery<GetAscProjectsQuery, GetAscProjectsQueryVariables>({
      query: GET_ASC_PROJECTS,
      variables: { companyId, fiscalYearId, periodName },
    });
    if (ascProjects) {
      const updatedEdges = ascProjects.getAscProjects.edges.map((edge) => {
        if (edge.node.product?.id === productToDelete?.id) {
          return { ...edge, node: { ...edge.node, product: null } };
        }
        return edge;
      });
      cache.writeQuery({
        query: GET_ASC_PROJECTS,
        variables: { companyId, fiscalYearId, periodName },
        data: { getAscProjects: { ...ascProjects.getAscProjects, edges: updatedEdges } },
      });
    }
  };

  const onUpdateProduct: MutationUpdaterFunction<
    UpdateProductMutation,
    Exact<{
      productId: string;
    }>,
    DefaultContext,
    ApolloCache<any>
  > = (cache, { data }): void => {
    const ascProjects = cache.readQuery<GetAscProjectsQuery, GetAscProjectsQueryVariables>({
      query: GET_ASC_PROJECTS,
      variables: { companyId, fiscalYearId, periodName },
    });
    if (ascProjects) {
      const updatedEdges = ascProjects.getAscProjects.edges.map((edge) => {
        if (edge.node.product?.id === productToEdit?.id) {
          return { ...edge, node: { ...edge.node, product: data?.updateProduct } };
        }
        return edge;
      });
      cache.writeQuery({
        query: GET_ASC_PROJECTS,
        variables: { companyId, fiscalYearId, periodName },
        data: { getAscProjects: { ...ascProjects.getAscProjects, edges: updatedEdges } },
      });
    }
  };

  const onValid: SubmitHandler<AscProjectInfoForm> = useCallback(
    (values): void => {
      updateAscProject(
        {
          name: values.name,
          description: values.description,
          productId: values.productId ?? null,
        },
        {
          update: (cache) => {
            evictCache(cache, ['getAscWages', 'getAscWageSummary']);
          },
        },
      );
    },
    [updateAscProject],
  );

  const onInvalid = (): void => {
    ToastConfigState.pushErrorToast('Error updating project', {
      description: 'One or more values in the form are invalid. Please correct them to save.',
      duration: 'MEDIUM',
    });
  };
  const onInvalidDebounced = useDebouncedCallback(onInvalid, 500);

  useEffect(() => {
    reset(makeDefaultFormValues(ascProject));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [ascProject.id, reset, ascProject.product?.id]);

  useEffect(() => {
    const subscription = watch(() => handleSubmit((data) => onValid(data), onInvalidDebounced)());
    return (): void => subscription.unsubscribe();
  }, [handleSubmit, onInvalidDebounced, onValid, watch, ascProject.id]);

  const formatCapitalizationDate = (date: Date | null): string => getCapitalizationDateString(date) || '—';

  return (
    <>
      <Stack as="form" direction="column" onSubmit={noopFormSubmit}>
        <ControlledInput
          control={control}
          name="name"
          id={nameId}
          label="Project Title"
          placeholder="Improve Website UI"
          rules={{ required: 'Name is required' }}
        />
        <InputFormContainer label="Project Description" inputId={descriptionId}>
          <ControlledTextArea
            control={control}
            name="description"
            id={descriptionId}
            placeholder="Building something amazing!"
            rows={4}
          />
        </InputFormContainer>
        <ControlledAutocompleteInput
          label="Product"
          name="productId"
          options={productOptions}
          optionValueKey="id"
          optionDisplayKey="name"
          searchKeys={['name']}
          placeholder="Type product name"
          id={productId}
          control={control}
          allowCreate
          onCreate={(value): void => {
            createProduct(value, { ascProjectId: ascProject.id });
          }}
          action={
            ascProject.product?.id && (
              <Typography
                as="button"
                fontSize="sm"
                color={colors.primary[600]}
                onClick={handleClearProduct}
                id={clearProductBtnId}
              >
                Clear
              </Typography>
            )
          }
        />

        {/* Project dates read-only display */}
        <InputFormContainer label="Project Dates" inputId="project-dates-display">
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={6} paddingX={3} paddingY={1}>
            <StyledProjectDatesSpacerStack direction="column" gap={0.5}>
              <Typography fontSize="xs" color={colors.gray[500]}>
                Source
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography fontSize="sm" color={colors.gray[900]}>
                  Auto-calculated
                </Typography>
                <Tooltip text={PROJECT_DATES_DESCRIPTION}>
                  <Stack direction="row" alignItems="center">
                    <InfoIcon width={14} height={14} color={colors.gray[400]} />
                  </Stack>
                </Tooltip>
              </Stack>
            </StyledProjectDatesSpacerStack>
            <StyledProjectDatesStack direction="row" gap={4} justifyContent="end">
              <StyledDateCellStack direction="column" gap={0.5}>
                <Typography fontSize="xs" color={colors.gray[500]}>
                  Start
                </Typography>
                {ascProject.startDate ? (
                  <Typography fontSize="sm" color={colors.gray[900]}>
                    {formatCapitalizationDate(ascProject.startDate)}
                  </Typography>
                ) : (
                  <Tooltip text="This date has not yet been determined.">
                    <Typography fontSize="sm" color={colors.gray[900]}>
                      —
                    </Typography>
                  </Tooltip>
                )}
              </StyledDateCellStack>
              <StyledDateCellStack direction="column" gap={0.5}>
                <Typography fontSize="xs" color={colors.gray[500]}>
                  End
                </Typography>
                {ascProject.endDate ? (
                  <Typography fontSize="sm" color={colors.gray[900]}>
                    {formatCapitalizationDate(ascProject.endDate)}
                  </Typography>
                ) : (
                  <Tooltip text="This date has not yet been determined.">
                    <Typography fontSize="sm" color={colors.gray[900]}>
                      —
                    </Typography>
                  </Tooltip>
                )}
              </StyledDateCellStack>
            </StyledProjectDatesStack>
          </Stack>
        </InputFormContainer>

        {/* Capitalization read-only display with edit button */}
        <InputFormContainer
          label="Capitalization"
          inputId="capitalization-display"
          action={
            <Typography
              as="button"
              fontSize="sm"
              color={colors.primary[600]}
              onClick={() => setIsCapitalizationModalOpen(true)}
            >
              Edit
            </Typography>
          }
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={6} paddingX={3} paddingY={1}>
            <StyledCapitalizationStatusStack direction="column" gap={0.5}>
              <Typography fontSize="xs" color={colors.gray[500]}>
                Status
              </Typography>
              <Typography fontSize="sm" color={colors.gray[900]}>
                {ascProject.isCapitalized ? 'Capitalized' : 'Expensed'}
              </Typography>
            </StyledCapitalizationStatusStack>
            {ascProject.isCapitalized && (
              <>
                <StyledCapitalizationDatesStack direction="column">
                  <Stack direction="row" gap={4} justifyContent="end">
                    <StyledDateCellStack direction="column" gap={0.5}>
                      <Typography fontSize="xs" color={colors.gray[500]}>
                        Start
                      </Typography>
                      <Typography fontSize="sm" color={colors.gray[900]}>
                        {formatCapitalizationDate(ascProject.capitalizationStartDate)}
                      </Typography>
                    </StyledDateCellStack>
                    <StyledDateCellStack direction="column" gap={0.5}>
                      <Typography fontSize="xs" color={colors.gray[500]}>
                        End
                      </Typography>
                      <Typography fontSize="sm" color={colors.gray[900]}>
                        {formatCapitalizationDate(ascProject.capitalizationEndDate)}
                      </Typography>
                    </StyledDateCellStack>
                  </Stack>
                </StyledCapitalizationDatesStack>
              </>
            )}
          </Stack>
        </InputFormContainer>
      </Stack>
      <DeleteProductModal product={productToDelete} onClose={clearSelectedProductState} onDelete={onDeleteProduct} />
      <UpdateProductModal product={productToEdit} onClose={clearSelectedProductState} onUpdate={onUpdateProduct} />
      <CapitalizationEditModal
        isOpen={isCapitalizationModalOpen}
        onClose={() => setIsCapitalizationModalOpen(false)}
        ascProject={ascProject}
      />
    </>
  );
};
